/**
 * Natural-language song input parsing — moved VERBATIM from SongNotes.js
 * (the known-show/known-artist lists included; they are curated data, not
 * code smell). Pure function, no React.
 *
 * Examples:
 *   "popular wicked"             → { song: "popular", show: "wicked" }
 *   "defying gravity from wicked"→ { song: "defying gravity", show: "wicked" }
 *   "popular by wicked"          → { song: "popular", artist: "wicked" }
 *   "bohemian rhapsody queen"    → { song: "bohemian rhapsody", artist: "queen" }
 */

// Known Broadway shows/musicals for smart detection
const knownShows = [
	'wicked', 'hamilton', 'phantom', 'phantom of the opera', 'les mis', 'les miserables',
	'les misérables', 'cats', 'rent', 'chicago', 'grease', 'hairspray', 'mamma mia',
	'dear evan hansen', 'mean girls', 'beetlejuice', 'hadestown', 'six', 'frozen',
	'lion king', 'the lion king', 'aladdin', 'little mermaid', 'beauty and the beast',
	'newsies', 'book of mormon', 'come from away', 'waitress', 'kinky boots',
	'annie', 'matilda', 'mary poppins', 'sound of music', 'west side story',
	'fiddler on the roof', 'oklahoma', 'carousel', 'south pacific', 'guys and dolls',
	'anything goes', 'hello dolly', 'funny girl', 'gypsy', 'cabaret', 'chicago',
	'a chorus line', 'company', 'follies', 'sweeney todd', 'into the woods',
	'sunday in the park', 'merrily we roll along', 'passion', 'assassins',
	'spring awakening', 'next to normal', 'fun home', 'jagged little pill',
	'moulin rouge', 'pretty woman', 'tootsie', 'mrs doubtfire', 'back to the future',
	'legally blonde', 'shrek', 'spongebob', 'be more chill', 'heathers',
	'21 chump street', 'in the heights', 'tick tick boom', 'jonathan larson',
	'little shop of horrors', 'rocky horror', 'jesus christ superstar', 'godspell',
	'evita', 'joseph', 'dreamgirls', 'jersey boys', 'motown', 'beautiful',
	'tina', 'cher', 'summer', 'donna summer', "ain't too proud", 'temptations',
	'suffs', 'great gatsby', 'gatsby', 'some like it hot', 'kimberly akimbo',
	'shucked', 'new york new york', 'sweeney', 'parade', 'ragtime', 'once',
	'bridges of madison county', 'light in the piazza', 'floyd collins',
	'the last five years', 'last 5 years', 'songs for a new world', 'jason robert brown',
	'falsettos', 'march of the falsettos', 'falsettoland', 'spelling bee',
	'25th annual putnam county spelling bee', 'dogfight', 'edges', 'bare',
	'if/then', 'giant', 'bright star', 'bandstand', 'the prom', 'prom',
	'girl from the north country', 'almost famous', 'water for elephants',
	'the notebook', 'notebook', 'the outsiders', 'outsiders', 'illinoise',
	"hell's kitchen", 'hells kitchen', "the who's tommy", 'tommy', 'cabaret',
	'sunset boulevard', 'sunset blvd',
];

// Known artists for pop/rock detection
const knownArtists = [
	'queen', 'beatles', 'the beatles', 'elton john', 'billy joel', 'adele',
	'taylor swift', 'beyonce', 'beyoncé', 'ariana grande', 'ed sheeran',
	'bruno mars', 'lady gaga', 'katy perry', 'rihanna', 'whitney houston',
	'celine dion', 'céline dion', 'mariah carey', 'michael jackson',
	'prince', 'david bowie', 'freddie mercury', 'stevie wonder',
	'barbra streisand', 'frank sinatra', 'ella fitzgerald', 'billie holiday',
	'aretha franklin', 'diana ross', 'cher', 'dolly parton', 'carole king',
	'joni mitchell', 'fleetwood mac', 'abba', 'bee gees', 'eagles',
	'journey', 'foreigner', 'chicago', 'earth wind fire', 'earth wind and fire',
	'peter gabriel', 'phil collins', 'genesis', 'led zeppelin', 'pink floyd',
	'rolling stones', 'the rolling stones', 'u2', 'coldplay', 'radiohead',
	'nirvana', 'pearl jam', 'foo fighters', 'green day', 'blink 182',
	'linkin park', 'evanescence', 'paramore', 'panic at the disco',
	'fall out boy', 'my chemical romance', 'twenty one pilots',
	'imagine dragons', 'maroon 5', 'onerepublic', 'the script',
	'hozier', 'sam smith', 'john legend', 'alicia keys', 'norah jones',
	'amy winehouse', 'duffy', 'florence and the machine', 'florence',
	'lana del rey', 'lorde', 'billie eilish', 'olivia rodrigo', 'dua lipa',
	'lizzo', 'doja cat', 'sza', 'the weeknd', 'post malone', 'harry styles',
	'shawn mendes', 'charlie puth', 'lewis capaldi', 'noah kahan',
];

export const parseNaturalInput = (input) => {
	if (!input) return { song: '', show: '', artist: '' };

	const trimmed = input.trim();

	// Check for explicit patterns first: "from", "by", "-"
	const fromMatch = trimmed.match(/^(.+?)\s+from\s+(.+)$/i);
	if (fromMatch) {
		return { song: fromMatch[1].trim(), show: fromMatch[2].trim(), artist: '' };
	}

	const byMatch = trimmed.match(/^(.+?)\s+by\s+(.+)$/i);
	if (byMatch) {
		return { song: byMatch[1].trim(), show: '', artist: byMatch[2].trim() };
	}

	const dashMatch = trimmed.match(/^(.+?)\s*[-–—]\s*(.+)$/);
	if (dashMatch) {
		// Could be "Song - Artist" or "Song - Show", check against known lists
		const secondPart = dashMatch[2].trim().toLowerCase();
		if (knownShows.includes(secondPart)) {
			return { song: dashMatch[1].trim(), show: dashMatch[2].trim(), artist: '' };
		}
		return { song: dashMatch[1].trim(), show: '', artist: dashMatch[2].trim() };
	}

	// No explicit separator — try to detect show/artist at the end
	const words = trimmed.split(/\s+/);
	if (words.length >= 2) {
		for (let i = words.length - 1; i >= 1; i--) {
			const suffix = words.slice(i).join(' ').toLowerCase();
			const prefix = words.slice(0, i).join(' ');

			if (knownShows.includes(suffix)) {
				return { song: prefix, show: words.slice(i).join(' '), artist: '' };
			}
			if (knownArtists.includes(suffix)) {
				return { song: prefix, show: '', artist: words.slice(i).join(' ') };
			}
		}
	}

	// No match found — return as-is (just the song title)
	return { song: trimmed, show: '', artist: '' };
};
